import React, { useEffect, useMemo, useState } from 'react';
import { useShipments } from '../../adapters/use-shipments';
import { GetShipmentsQuery, Shipment, ShipmentStatus, SortableShipmentKey } from '../../ports/ShipmentsService';
import { useCurrentLocation } from '../../store/hooks/useCurrentLocation';
import InternalShipmentsTable, { Columns } from './InternalShipmentsTable';
import { equals } from 'ramda';
import { LocaleSettings } from '../../models/location';

// tslint:disable-next-line:import-name
import style from './index.module.css';
import { isNonNullable } from '../../common/utils';

export interface ShipmentsTableProps {
  carrierAccountKeys?: string[];
  status?: ShipmentStatus[];
  palletBoxId?: string;
  isSorted?: boolean;
  isVoided?: boolean;
  tags?: string[];
  initialSort?: [SortableShipmentKey, 'ASC' | 'DESC'][];
  hideColumns?: Columns[];
  FiltersComponent?: (props: {
    localeSettings: LocaleSettings;
    onFiltersChange: (query: GetShipmentsQuery) => void;
    style: string;
  }) => JSX.Element;
  SelectionComponent?: (props: {
    selection: Shipment[];
    clearSelection: () => void;
  }) => JSX.Element;
  className?: string;
}

export function createShipmentsTable(
  {
    useShipments,
  }: {
    useShipments: (query?: GetShipmentsQuery) => {
      shipments: Shipment[];
      isLoading: boolean;
      page: number;
      pages: number;
      error?: Error;
    },
  },
) {
  // tslint:disable-next-line:function-name
  return function ShipmentsTable(
    {
      FiltersComponent,
      SelectionComponent,
      ...props
     }: ShipmentsTableProps = {},
  ) {
    const { localeSettings } = useCurrentLocation();
    const [limit, setLimit] = useState(10);
    const [page, setPage] = useState(1);
    const [sort, setSort] = useState<[SortableShipmentKey, 'ASC' | 'DESC'][] | undefined>(
      props.initialSort,
    );
    const [query, setQuery] = useState<GetShipmentsQuery | undefined>(undefined);
    const [selection, setSelection] = useState<Record<string, Shipment | undefined>>({});

    const {
      pages,
      shipments,
      isLoading,
    } = useShipments(query);

    const toggleSelection = (shipment: Shipment) => {
      if (selection[shipment.id]) {
        setSelection({ ...selection, [shipment.id]: undefined });
      } else {
        setSelection({ ...selection, [shipment.id]: shipment });
      }
    };

    const toggleAll = () => {
      const newSelection = { ...selection };
      if (shipments.some(shipment => !selection[shipment.id])) {
        shipments.forEach(shipment => { newSelection[shipment.id] = shipment; });
      } else {
        shipments.forEach(shipment => { newSelection[shipment.id] = undefined; });
      }
      setSelection(newSelection);
    };

    const isSelected = (shipmentId: string) => !!selection[shipmentId];

    const selectedList = useMemo(
      () => Object.values(selection).filter(isNonNullable),
      [selection],
    );

    const updateQuery = useMemo(
      () => {
        const localFilters: GetShipmentsQuery = {
          limit,
          page,
          sort,
        };

        if (props.carrierAccountKeys) {
          localFilters.carrierAccountKeys = props.carrierAccountKeys;
        }
        if (props.tags) {
          localFilters.tags = props.tags;
        }
        if (props.status) {
          localFilters.status = props.status;
        }
        if (props.isSorted !== undefined) {
          localFilters.isSorted = props.isSorted;
        }
        if (props.isVoided !== undefined) {
          localFilters.isVoided = props.isVoided;
        }
        if (props.palletBoxId !== undefined) {
          localFilters.palletBoxId = props.palletBoxId;
        }

        return (filters: GetShipmentsQuery) => {
          const q: GetShipmentsQuery = {
            ...filters,
            ...localFilters,
            tags: [...localFilters.tags ?? [], ...filters.tags ?? []],
          };
          if (!equals(q, query)) {
            setQuery(q);
          }
        };
      },
      [sort, limit, page, props],
    );

    useEffect(
      () => {
        if (!FiltersComponent && !query) {
          updateQuery({});
        }
      },
      [updateQuery, FiltersComponent],
    );

    useEffect(
      () => {
        if (query) {
          updateQuery(query);
        }
      },
      [sort, limit, page],
    );

    useEffect(
      () => {
        setPage(Math.min(pages, page));
      },
      [pages, page],
    );

    return <>
    <div className={style.Header}>
      {FiltersComponent ?
        <FiltersComponent localeSettings={localeSettings} onFiltersChange={updateQuery} style={style.Filters} /> :
        null}
      {SelectionComponent ?
        <SelectionComponent clearSelection={() => setSelection({})} selection={selectedList} /> :
        null}
    </div>
      <InternalShipmentsTable
        className={props.className}
        localeSettings={localeSettings}
        onPageSizeChange={(size, page) => {
          setLimit(size);
          setPage(page + 1);
        }}
        hideColumns={props.hideColumns}
        onPageChange={page => setPage(page + 1)}
        onSortChange={setSort}
        loading={isLoading}
        pages={pages}
        data={shipments}
        toggleAll={toggleAll}
        toggleSelection={toggleSelection}
        isSelected={SelectionComponent ? isSelected : undefined}
      />
    </>;
  };
}

// tslint:disable-next-line:variable-name
export const ShipmentsTable = createShipmentsTable({ useShipments });
